import { Box, Button, Tooltip, Typography } from '@mui/material';
import { Map } from 'immutable';
import { IStory } from '@opengov/stories-shared/types';
import React, { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { ESelectAllStatus, IStoriesFolder, IStoryListMessages, IStoryListSelection, IsubjectClaimsMessages } from '../../../../types';
import { useUpdatePublicationUpdatedAt } from '../../Publications/PublicationView/hooks/usePublicationMutations';
import { StoriesSelectionContext } from '../../Publications/PublicationView/context/StoriesSelectionContext';
import { BulkDeleteStoryModal } from '../BulkDeleteStoryModal';
import { ChangeStatusModal } from '../ChangeStatusModal';
import { CopyStoriesModal } from '../CopyStoriesModal';
import { MoveStoryModal } from '../MoveStoryModal';
import { useStoryListContext } from '../storyListContext';
import { BulkStoryGranularAccess } from '../../../../modals/components/BulkStoryGranularAccess';
import { StoryGranularAccess } from '../../../../modals/components/StoryGranularAccess';

import { styles as muiStyles } from './SelectionToolbar.styles';

export interface ISelectionToolbarProps {
  entityId: number;
  entityUuid?: string;
  entitySubdomain: string;
  canCopyIndividualStories: boolean;
  messages: IStoryListMessages;
  subjectClaimsMessages?: IsubjectClaimsMessages
  currentFolderId: string;
  folderStructure: IStoriesFolder[];
  publicationFolderStructure?: IStoriesFolder[];
  publicationFoldersTree?: IStoriesFolder[];
  selectAllStories?: boolean;
  totalStoriesCount?: number;
  currentPageStoriesCount?: number;
  sectionId?: string;
  searchText?: string;
  storyState?: string;
  show?: string;
  publicationId?: string;
  userId?: number;
  publicationSelectedStories?: Map<string, IStory>;
  publicationStoryListing?: boolean;
  isBulkUpdateStoryStatusEnabled?: boolean;
  publicationSetSelectedStories?(stories: Map<string, IStory>): void;
  publicationSetSelectAllStatus?(selectAllStatus: ESelectAllStatus): void;
  setSectionId?(section: IStoryListSelection | null): void;
  storiesAccessControlEnabled?: boolean;
  canFollowStories?: boolean;
  isAdmin?: boolean;
  openModal?(modal: string): IAction<string, string>;
  onStoriesRefresh?(): void;
}

const handleUnselectAll = (setSelectAllStatus, setSelectedStories): void => {
  setSelectedStories(Map([]));
  setSelectAllStatus(ESelectAllStatus.None);
};

export const handleSelectAll = (
  setSelectedStories,
  setSelectAllStatus
): void => {
  setSelectedStories(Map([]));
  setSelectAllStatus(ESelectAllStatus.All);
};

export const SelectionToolbar: React.FC<ISelectionToolbarProps> = ({
  entityId,
  entityUuid,
  entitySubdomain,
  canCopyIndividualStories,
  folderStructure,
  publicationFolderStructure,
  publicationFoldersTree = [],
  selectAllStories,
  totalStoriesCount,
  currentPageStoriesCount,
  currentFolderId,
  messages,
  publicationId,
  userId,
  publicationSelectedStories,
  publicationSetSelectedStories,
  publicationSetSelectAllStatus,
  publicationStoryListing,
  isBulkUpdateStoryStatusEnabled,
  sectionId,
  searchText,
  storyState,
  show,
  setSectionId,
  isAdmin,
  storiesAccessControlEnabled,
  subjectClaimsMessages,
  canFollowStories,
  openModal,
  onStoriesRefresh
}) => {
  const storyListContext = useStoryListContext();
  const selectionCtx = useContext(StoriesSelectionContext);
  const updatePublicationUpdatedAtMutation = useUpdatePublicationUpdatedAt();
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isMoveStoryModalVisible, setIsMoveStoryModalVisible] = useState(false);
  const [isCopyStoriesModalVisible, setIsCopyStoriesModalVisible] = useState(false);
  const [isChangeStatusModalVisible, setIsChangeStatusModalVisible] = useState(false);
  const [isAccessModalVisible, setIsAccessModalVisible] = useState(false);
  const [isSelectionInPublication, setIsSelectionInPublication] = useState(false);

  const { selectedStories, setSelectAllStatus, setSelectedStories } = storyListContext;

  const selectedStoryIds = publicationStoryListing
    ? publicationSelectedStories.keySeq().toArray()
    : selectedStories.keySeq().toArray();

  const handleOpenDialog = (e) => {
    e.preventDefault();
    setIsAccessModalVisible(true);
  };

  useEffect(() => {
    if (!publicationStoryListing) {
      const selectedStoriesValue = [...selectedStories.values()];
      setIsSelectionInPublication(selectedStoriesValue.some(story => !!story.folder?.publicationId));
    }
  }, [selectedStories]);

  return (
    <>
      {isChangeStatusModalVisible && publicationStoryListing &&
        createPortal(
          <ChangeStatusModal
            messages={messages}
            publicationId={publicationId}
            entityId={entityId}
            entityUuid={entityUuid}
            selectedStoriesIds={selectedStoryIds}
            selectAllStories={selectAllStories}
            sectionId={sectionId}
            searchText={searchText}
            storyState={storyState}
            show={show}
            onClose={() => setIsChangeStatusModalVisible(false)}
            onSuccess={() => {
              handleUnselectAll(publicationSetSelectAllStatus, publicationSetSelectedStories);
              selectionCtx?.setSelectAll(false);
            }}
            canFollowStories={canFollowStories}
          />,
          document.getElementById('app')
        )}
      {isAccessModalVisible && publicationStoryListing && publicationSelectedStories &&
        createPortal(
          // Single story → per-story dialog (preloads + view/edit/remove existing access);
          // 2+ stories → bulk add-only modal.
          publicationSelectedStories.size === 1 ? (
            <StoryGranularAccess
              entitySubdomain={entitySubdomain}
              closeModal={() => setIsAccessModalVisible(false)}
              storyId={selectedStoryIds[0]}
              isTemplate={false}
              entityId={entityId}
              messages={subjectClaimsMessages}
              publicationId={publicationId}
              onSuccess={() => {
                handleUnselectAll(publicationSetSelectAllStatus, publicationSetSelectedStories);
                selectionCtx?.setSelectAll(false);
              }}
            />
          ) : (
            <BulkStoryGranularAccess
              entitySubdomain={entitySubdomain}
              closeModal={() => setIsAccessModalVisible(false)}
              selectedStories={publicationSelectedStories}
              messages={subjectClaimsMessages}
              publicationId={publicationId}
              onSuccess={() => {
                handleUnselectAll(publicationSetSelectAllStatus, publicationSetSelectedStories);
                selectionCtx?.setSelectAll(false);
              }}
            />
          ),
          document.getElementById('app')
        )}
      {isDeleteModalVisible &&
        createPortal(
          <BulkDeleteStoryModal
            messages={messages}
            selectedStoryIds={selectedStoryIds}
            publicationId={publicationId}
            entityId={entityId}
            onDeleteSuccess={() => {
              if (publicationStoryListing) {
                handleUnselectAll(publicationSetSelectAllStatus, publicationSetSelectedStories);
                selectionCtx?.setSelectAll(false);
              } else {
                handleUnselectAll(setSelectAllStatus, setSelectedStories);
                onStoriesRefresh?.();
              }
              updatePublicationUpdatedAtMutation.mutate({ publicationId, userId });
            }}
            onClose={() => setIsDeleteModalVisible(false)}
          />,
          document.getElementById('app')
        )}
      {isCopyStoriesModalVisible &&
        createPortal(
          <CopyStoriesModal
            entityId={entityId}
            entitySubdomain={entitySubdomain}
            messages={messages}
            selectedStories={publicationStoryListing ? publicationSelectedStories : selectedStories}
            handleUnselectAll={() => {
              if (publicationStoryListing) {
                handleUnselectAll(publicationSetSelectAllStatus, publicationSetSelectedStories);
                selectionCtx?.setSelectAll(false);
              } else {
                handleUnselectAll(setSelectAllStatus, setSelectedStories);
                onStoriesRefresh?.();
              }
            }}
            folderStructure={folderStructure}
            publicationFoldersTree={publicationFoldersTree}
            currentFolderId={currentFolderId}
            onClose={() => setIsCopyStoriesModalVisible(false)}
            isPublicationCopyStories={publicationStoryListing}
            userId={userId}
            sectionId={sectionId}
            setSectionId={setSectionId}
          />,
          document.getElementById('app')
        )}
      {isMoveStoryModalVisible &&
        createPortal(
          <MoveStoryModal
            messages={messages}
            selectedStories={publicationStoryListing ? publicationSelectedStories : selectedStories}
            folderStructure={publicationStoryListing ? publicationFolderStructure : folderStructure}
            currentFolderId={currentFolderId}
            entitySubdomain={entitySubdomain}
            publicationId={publicationId}
            handleMoveStoriesSuccess={() => {
              if (publicationStoryListing) {
                handleUnselectAll(publicationSetSelectAllStatus, publicationSetSelectedStories);
                selectionCtx?.setSelectAll(false);
                updatePublicationUpdatedAtMutation.mutate({ publicationId, userId });
              } else {
                handleUnselectAll(setSelectAllStatus, setSelectedStories);
                onStoriesRefresh?.();
              }
            }}
            onClose={() => {
              setIsMoveStoryModalVisible(false);
            }}
          />,
          document.getElementById('app')
        )}
      <Box sx={muiStyles.selectControls} data-test="selection-toolbar">
        <section aria-label="Story selection">
          <Typography component="span" sx={muiStyles.groupLabel}>{messages.manageStories}</Typography>
          <Box sx={muiStyles.toolbarSelectionInfo}>
            {publicationStoryListing ? (
              <Typography variant="body2" fontWeight={500} data-test="stories-selected-text">
                {selectAllStories && (publicationSelectedStories.size < totalStoriesCount)
                  ? messages.storiesSelected({ value: totalStoriesCount })
                  : publicationSelectedStories.size === 1
                    ? messages.storySelected({ value: publicationSelectedStories.size })
                    : messages.storiesSelected({ value: publicationSelectedStories.size })}
              </Typography>
            ) : (
              <Typography variant="body2" fontWeight={500} data-test="stories-selected-text">
                {selectedStories.size === 1
                  ? messages.storySelected({ value: selectedStories.size })
                  : messages.storiesSelected({ value: selectedStories.size })}
              </Typography>
            )}
            <Box sx={muiStyles.divider} />
            {publicationStoryListing ? (
              <>
                {!selectAllStories && isBulkUpdateStoryStatusEnabled && (currentPageStoriesCount !== totalStoriesCount) && (
                  <>
                    <Box
                      component="button"
                      tabIndex={0}
                      data-test="select-all-stories-button"
                      sx={muiStyles.unselectButton}
                      onClick={() => {
                        handleSelectAll(publicationSetSelectedStories, publicationSetSelectAllStatus);
                        selectionCtx?.setSelectAll(true);
                      }}
                    >
                      {currentFolderId
                        ? messages.selectAllSectionStories({ totalStoriesCount: totalStoriesCount?.toString() })
                        : messages.selectAllStories({ totalStoriesCount: totalStoriesCount?.toString() })}
                    </Box>
                    <Box sx={muiStyles.divider} />
                  </>
                )}
                <Box
                  component="button"
                  tabIndex={0}
                  data-test="unselect-stories-button"
                  sx={muiStyles.unselectButton}
                  onClick={() => {
                    handleUnselectAll(publicationSetSelectAllStatus, publicationSetSelectedStories);
                    selectionCtx?.setSelectAll(false);
                  }}
                >
                  {messages.clearSelection}
                </Box>
              </>
            ) : (
              <Box
                component="button"
                tabIndex={0}
                data-test="unselect-stories-button"
                sx={muiStyles.unselectButton}
                onClick={() => handleUnselectAll(setSelectAllStatus, setSelectedStories)}
              >
                {messages.clearSelection}
              </Box>
            )}
          </Box>
        </section>
        <Box sx={muiStyles.buttons}>
          {isBulkUpdateStoryStatusEnabled && (
            <Button variant="outlined" color="secondary" size="small" data-test="change-status-button" onClick={() => setIsChangeStatusModalVisible(true)}>
              {messages.changeStatus}
            </Button>
          )}
          {storiesAccessControlEnabled && isAdmin && (
            <Button variant="outlined" color="secondary" size="small" data-test="manage-access" onClick={(e) => handleOpenDialog(e)}>
              {subjectClaimsMessages?.manageAccess}
            </Button>
          )}
          {!selectAllStories && (
            <>
              <Tooltip
                placement="bottom"
                title={isSelectionInPublication ? messages.moveStoriesDisabledNote : ''}
                data-test="tooltip-move-stories-note"
              >
                <Box component="span">
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    data-test="move-stories-button"
                    onClick={() => setIsMoveStoryModalVisible(true)}
                    disabled={isSelectionInPublication}
                  >
                    {messages.move}
                  </Button>
                </Box>
              </Tooltip>
              {canCopyIndividualStories && (
                <Button variant="outlined" color="secondary" size="small" data-test="copy-stories-button" onClick={() => setIsCopyStoriesModalVisible(true)}>
                  {messages.copy}
                </Button>
              )}
            </>
          )}
          {!selectAllStories && (
            <Tooltip
              placement="bottom"
              title={isSelectionInPublication ? messages.deleteStoriesDisabledNote : ''}
              data-test="tooltip-delete-stories-note"
            >
              <Box component="span">
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  data-test="delete-stories-button"
                  onClick={() => setIsDeleteModalVisible(true)}
                  disabled={isSelectionInPublication}
                >
                  {messages.delete}
                </Button>
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>
    </>
  );
};
